import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  SessionForm,
  sessionFormSchema,
  sessionFormStrictSchema,
} from '@packages/shared';
import { useNavigate } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FormWrapper } from '@/components/ui+/FormWrapper';
import { NullableInput } from '@/components/ui+/NullableInput';
import { cn } from '@/utils/cn';
import { handleSubmitErrors } from '@/utils/handleSubmitErrors';

export const RegistrationForm = ({
  companyLocationCode,
  siteCode,
  termsAndConditions,
}: {
  companyLocationCode: string;
  siteCode: string;
  /** The company's terms, or `null` when it has none and registration is not gated. */
  termsAndConditions: string | null;
}) => {
  const convex = useConvex();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDev = import.meta.env.VITE_STAGE !== 'production';

  const newSessionForm = sessionFormSchema.decode({
    firstName: isDev ? 'John' : '',
    lastName: isDev ? 'Doe' : '',
    mobileNumber: isDev ? '0477123456' : '',
    companyLocationCode,
    siteCode,
  });

  const form = useForm({
    defaultValues: newSessionForm,
    resolver: zodResolver(sessionFormStrictSchema),
  });

  // `useWatch` (not `form.watch`) so the Register button re-renders as the box is ticked.
  const termsAccepted = useWatch({ control: form.control, name: 'termsAccepted' });
  const isBlockedByTerms = Boolean(termsAndConditions) && !termsAccepted;

  const onValidSubmit = async (values: SessionForm) => {
    setIsSubmitting(true);
    try {
      await convex.mutation(api.sessions.createSession, values);
      await navigate({
        to: '/$companyLocationCode/$siteCode/sms-sent',
        params: { companyLocationCode, siteCode },
        search: { mobile: values.mobileNumber },
      });
    } catch (error) {
      toast.error(getUserErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <FormWrapper {...form}>
        <form className="space-y-5" onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First name</FormLabel>
                <FormControl>
                  <Input size="lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last name</FormLabel>
                <FormControl>
                  <Input size="lg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="mobileNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile number</FormLabel>
                <FormControl>
                  <Input size="lg" type="tel" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="estimatedCheckoutDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estimated check out (optional)</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        size="lg"
                        className={cn(
                          'w-full justify-start bg-white/85 text-left font-normal',
                          !field.value && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 size-4" />
                        {field.value ? format(new Date(field.value), 'PPP') : 'Select date'}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ? new Date(field.value) : undefined}
                      onSelect={(date) => {
                        field.onChange(date ? format(date, 'yyyy-MM-dd') : null);
                      }}
                      disabled={(date) => date < new Date(new Date().toDateString())}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email (optional)</FormLabel>
                <FormControl>
                  <NullableInput size="lg" type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {termsAndConditions ? (
            <FormField
              control={form.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem>
                  <p className="font-medium text-sm">Terms and conditions</p>
                  {/* `whitespace-pre-line` keeps the paragraph breaks of the stored plain text. */}
                  <div className="max-h-56 overflow-y-auto whitespace-pre-line rounded-md border bg-muted/40 p-3 text-muted-foreground text-sm">
                    {termsAndConditions}
                  </div>
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                        className="mt-0.5"
                      />
                    </FormControl>
                    <FormLabel className="font-normal leading-snug">
                      I've read and accept the terms and conditions
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          <ActionButton
            type="submit"
            className="w-full"
            size="lg"
            // `ActionButton` spreads props after its own `disabled`, so `isSubmitting`
            // has to be repeated here to keep the button disabled while submitting.
            disabled={isBlockedByTerms || isSubmitting}
            loading={isSubmitting}
          >
            Register
          </ActionButton>
        </form>
      </FormWrapper>
    </div>
  );
};
